import React, { useEffect, useState } from "react";
import { maintenanceApi, ProcManufacturer, ProcOrder } from "../../../api/maintenance";
import { errMsg } from "./procUtils";
import { useNotice } from "../shared/useNotice";

interface Props {
  deviceIds: number[];
  onClose: () => void;
  onDone: (orderNo: string) => void;
}

/**
 * Put selected devices on an order — either an existing draft or a brand new one.
 * Nothing is sent and no device moves until the order is issued.
 */
const AddToOrderForm: React.FC<Props> = ({ deviceIds, onClose, onDone }) => {
  const [drafts, setDrafts] = useState<ProcOrder[]>([]);
  const [manufacturers, setManufacturers] = useState<ProcManufacturer[]>([]);
  const [target, setTarget] = useState<string>("__new__");
  const [manufacturerId, setManufacturerId] = useState<string>("");
  const [note, setNote] = useState("");
  const [error, setError] = useNotice("error", "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      maintenanceApi.procGetOrders({ status: "draft", page_size: 100 }),
      maintenanceApi.procGetManufacturers(),
    ])
      .then(([o, m]) => {
        setDrafts(o.data.items);
        setManufacturers(m.data);
        if (o.data.items.length) setTarget(String(o.data.items[0].id));
        if (m.data.length) setManufacturerId(String(m.data[0].id));
      })
      .catch((e) => setError(errMsg(e, "Failed to load orders")));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (target === "__new__") {
        if (!manufacturerId) {
          setError("Pick a manufacturer — add one on the Manufacturers tab if the list is empty.");
          setSaving(false);
          return;
        }
        const res = await maintenanceApi.procCreateOrder({
          manufacturer_id: Number(manufacturerId),
          note: note.trim() || undefined,
          device_ids: deviceIds,
        });
        onDone(res.data.order_no);
      } else {
        const res = await maintenanceApi.procUpdateOrderItems(Number(target), { add: deviceIds });
        onDone(res.data.order_no);
      }
    } catch (err: any) {
      setError(errMsg(err, "Failed to add devices to the order"));
      setSaving(false);
    }
  };

  return (
    <div className="maint-form-overlay" onClick={onClose}>
      <div className="maint-form-card" onClick={(e) => e.stopPropagation()}>
        <div className="maint-form-header">
          <h3>
            Add {deviceIds.length} device{deviceIds.length === 1 ? "" : "s"} to an order
          </h3>
          <button className="maint-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="proc-mini" style={{ marginTop: 0 }}>
          An order is a document you can save, reopen and keep adding to. Nothing is sent and no
          device moves until you <b>issue</b> it.
        </p>

        <form onSubmit={handleSubmit}>
          <label>
            Order
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              {drafts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.order_no} · {d.manufacturer_name} · {d.total_items} item
                  {d.total_items === 1 ? "" : "s"}
                </option>
              ))}
              <option value="__new__">➕ New draft order…</option>
            </select>
          </label>

          {target === "__new__" && (
            <>
              <label>
                Manufacturer
                <select value={manufacturerId} onChange={(e) => setManufacturerId(e.target.value)}>
                  {manufacturers.length === 0 && <option value="">No manufacturers yet</option>}
                  {manufacturers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.email ? ` (${m.email})` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Note (optional)
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Q3 build"
                />
              </label>
            </>
          )}

          {error && <p className="maint-form-error">{error}</p>}

          <div className="maint-form-actions">
            <button type="button" className="maint-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="maint-btn-primary" disabled={saving}>
              {saving ? "Adding…" : "Add to order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddToOrderForm;
